import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useBrand } from '../../context/BrandContext'
import { useAuthContext } from '../../context/AuthContext'
import { useWindowSize, isMobile } from '../../hooks/useWindowSize'

export default function EmployeeLayout() {
  const { profile, signOut } = useAuth()
  const { switchDemoRole, profile: authProfile } = useAuthContext()
  const isDemoMode = authProfile?.demo_mode
  const { brand } = useBrand()
  const navigate = useNavigate()
  const { width } = useWindowSize()
  const mobile = isMobile(width)

  const accent  = brand.primaryColor  || '#B8734A'
  const text    = brand.textColor     || '#2B2B2B'
  const logoSize = brand.logoSize     || 'medium'
  const logoW   = logoSize === 'large' ? 48 : logoSize === 'small' ? 24 : 34
  const sidebar = brand.sidebarColor  || '#2B2B2B'
  const bg      = brand.bgColor       || '#F7F2EB'

  const initials = profile
    ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`
    : 'JM'

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }
  function handleSwitch() {
    const newRole = switchDemoRole()
    navigate(newRole === 'admin' ? '/admin' : '/dashboard', { replace: true })
  }

  const navStyle = ({ isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 18px',
    fontSize: 12,
    color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
    borderLeft: isActive ? `2px solid ${accent}` : '2px solid transparent',
    background: isActive ? `${accent}22` : 'transparent',
    textDecoration: 'none',
    transition: 'all 0.15s',
  })

  const bottomNavStyle = ({ isActive }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    padding: '8px 4px',
    flex: 1,
    textDecoration: 'none',
    color: isActive ? accent : 'rgba(43,43,43,0.4)',
    borderTop: isActive ? `2px solid ${accent}` : '2px solid transparent',
    background: 'transparent',
    fontSize: 9,
    fontWeight: isActive ? 600 : 400,
    transition: 'all 0.15s',
  })

  if (mobile) {
    return (
      <div style={{display:'flex',flexDirection:'column',minHeight:'100vh',background:bg,fontFamily:"'Montserrat',sans-serif"}}>
        {/* Mobile top bar */}
        <div style={{background:sidebar,padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100,flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {brand.logoUrl
              ? <img src={brand.logoUrl} alt="logo" style={{width:28,height:28,objectFit:'contain',borderRadius:4}}/>
              : <div style={{width:28,height:28,borderRadius:'50%',border:`1px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'Georgia,serif',fontSize:10,fontWeight:700,color:accent}}>{brand.monogram||'CS'}</div>
            }
            <div style={{fontFamily:'Georgia,serif',fontSize:13,color:'#fff',fontWeight:600}}>Collective Staff</div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            {(isDemoMode||profile) && (
              <button onClick={handleSwitch} style={{fontSize:9,padding:'4px 8px',background:'none',border:`1px solid rgba(255,255,255,0.2)`,borderRadius:4,color:'rgba(255,255,255,0.5)',cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                Admin
              </button>
            )}
            <div style={{width:30,height:30,borderRadius:'50%',background:`${accent}25`,border:`1.5px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:600,color:accent}}>
              {initials}
            </div>
          </div>
        </div>

        {/* Main content */}
        <main style={{flex:1,padding:'16px',paddingBottom:'80px',color:text,overflowY:'auto'}}>
          <Outlet />
        </main>

        {/* Bottom navigation bar */}
        <div style={{position:'fixed',bottom:0,left:0,right:0,background:'#fff',borderTop:'1px solid #EDE6DA',display:'flex',zIndex:100,boxShadow:'0 -2px 10px rgba(0,0,0,0.06)'}}>
          <NavLink to="/dashboard" end style={bottomNavStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            Home
          </NavLink>
          <NavLink to="/calendar" style={bottomNavStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Calendar
          </NavLink>
          <NavLink to="/memos" style={bottomNavStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Memos
          </NavLink>
          <NavLink to="/time-off" style={bottomNavStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Time Off
          </NavLink>
          <NavLink to="/documents" style={bottomNavStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Docs
          </NavLink>
          <NavLink to="/requests" style={bottomNavStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
            Requests
          </NavLink>
        </div>
      </div>
    )
  }

  return (
    <div style={{display:'grid',gridTemplateColumns:'220px 1fr',minHeight:'100vh',fontFamily:"'Montserrat',sans-serif"}}>
      <aside style={{background:sidebar,display:'flex',flexDirection:'column',position:'sticky',top:0,height:'100vh',overflowY:'auto'}}>
        <div style={{padding:'20px 18px 14px',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}} onClick={()=>navigate('/dashboard')}>
            {brand.logoUrl
              ? <img src={brand.logoUrl} alt="logo" style={{width:logoW,height:logoW,objectFit:'contain',borderRadius:4}}/>
              : <div style={{width:34,height:34,borderRadius:'50%',border:`1px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'Georgia,serif',fontSize:12,fontWeight:700,color:accent}}>{brand.monogram || 'CS'}</div>
            }
            <div>
              <div style={{fontFamily:'Georgia,serif',fontSize:14,color:'#fff',fontWeight:600,lineHeight:1}}>Collective Staff</div>
              <div style={{fontSize:9,color:'rgba(255,255,255,0.35)',letterSpacing:'0.12em',textTransform:'uppercase',marginTop:2}}>{brand.companyName || 'Demo Company'}</div>
            </div>
          </div>
        </div>
        <div style={{padding:'14px 18px',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
          <div style={{width:38,height:38,borderRadius:'50%',background:`${accent}25`,border:`1.5px solid ${accent}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:600,color:accent,marginBottom:7}}>{initials}</div>
          <div style={{fontSize:13,color:'#fff',fontWeight:500}}>{profile?.first_name} {profile?.last_name}</div>
          <div style={{fontSize:10,color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.1em'}}>Employee</div>
          {profile?.department && <div style={{fontSize:11,color:accent,marginTop:2}}>{profile.department}</div>}
          {(isDemoMode || profile) && (
            <button onClick={handleSwitch} style={{marginTop:8,padding:'4px 10px',background:'none',border:'1px solid rgba(184,115,74,0.27)',borderRadius:4,fontSize:10,color:'rgba(184,115,74,0.6)',cursor:'pointer',fontFamily:"'Montserrat',sans-serif",textAlign:'left'}}>
              Admin View &rarr;
            </button>
          )}
        </div>
        <nav style={{flex:1,padding:'10px 0'}}>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:'rgba(255,255,255,0.25)',fontWeight:500}}>My Workspace</div>
          <NavLink to="/dashboard" end style={navStyle}>Dashboard</NavLink>
          <NavLink to="/onboarding" style={navStyle}>
            Onboarding
            <span style={{marginLeft:'auto',background:accent,color:'#fff',fontSize:9,padding:'1px 5px',borderRadius:8,fontWeight:600}}>2</span>
          </NavLink>
          <NavLink to="/policies" style={navStyle}>
            Signed Documents
            <span style={{marginLeft:'auto',background:accent,color:'#fff',fontSize:9,padding:'1px 5px',borderRadius:8,fontWeight:600}}>1</span>
          </NavLink>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:'rgba(255,255,255,0.25)',fontWeight:500,marginTop:6}}>Time & Attendance</div>
          <NavLink to="/timesheets" style={navStyle}>Timesheets</NavLink>
          <NavLink to="/time-off" style={navStyle}>Time Off</NavLink>
          <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:'rgba(255,255,255,0.25)',fontWeight:500,marginTop:6}}>Communication</div>
          <NavLink to="/memos" style={navStyle}>
            Memos
            <span style={{marginLeft:'auto',background:accent,color:'#fff',fontSize:9,padding:'1px 5px',borderRadius:8,fontWeight:600}}>3</span>
          </NavLink>
          <NavLink to="/documents" style={navStyle}>Documents</NavLink>
          <NavLink to="/calendar" style={navStyle}>Calendar</NavLink>
          <NavLink to="/requests" style={navStyle}>
            Requests
            <span style={{marginLeft:'auto',background:accent,color:'#fff',fontSize:9,padding:'1px 5px',borderRadius:8,fontWeight:600}}>2</span>
          </NavLink>
          {brand.features?.roomScheduler && (
            <>
              <div style={{padding:'8px 18px 3px',fontSize:9,letterSpacing:'0.18em',textTransform:'uppercase',color:'rgba(255,255,255,0.25)',fontWeight:500,marginTop:6}}>{brand.campusLabel || 'Collective Campus'}</div>
              <NavLink to="/campus" style={navStyle}>Spaces & Availability</NavLink>
            </>
          )}
        </nav>
        <div style={{padding:'12px 18px',borderTop:'1px solid rgba(255,255,255,0.06)'}}>
          {(isDemoMode || profile) && (
            <button onClick={handleSwitch} style={{display:'block',width:'100%',marginBottom:8,padding:'5px 8px',background:'none',border:'1px solid rgba(255,255,255,0.15)',borderRadius:4,fontSize:10,color:'rgba(255,255,255,0.5)',cursor:'pointer',fontFamily:"'Montserrat',sans-serif",textAlign:'left'}}>
              Switch to Admin Portal
            </button>
          )}
          <button onClick={handleSignOut} style={{background:'none',border:'none',fontSize:11,color:'rgba(255,255,255,0.25)',cursor:'pointer',padding:0,fontFamily:"'Montserrat',sans-serif"}}>
            Sign out
          </button>
          <div style={{fontSize:10,color:'rgba(255,255,255,0.15)',marginTop:4}}>gallaghercollectiveworks.com</div>
        </div>
      </aside>
      <main style={{display:'flex',flexDirection:'column',minHeight:'100vh',background:bg,color:text}}>
        <div style={{flex:1,padding:'24px'}}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}
